<template>
  <FlotoContentLoader
    classes="h-100"
    :transition="useTransition ? undefined : 'none'"
    :loading="loading"
  >
    <SuggestedFieldValuesProvider
      :resource-list="[ticket]"
      :module-name="$constants.CHANGE"
    >
      <DetailView
        :default-sidebar-visible="true"
        :disabled="true"
        :is-ticket-closed="true"
        :update-api="update"
        :resource="ticket"
        :additional-info-col-size="9"
        :module-name="moduleName"
        :change-stage="changeStage"
        v-bind="{ ...$attrs, ...fieldLabels }"
      >
        <template v-slot:before-title>
          <FlotoBackButton
            :to="$currentModule.getRoute('my-changes')"
            class="text-neutral-light mr-2"
          />
        </template>

        <!-- system fields as details -->
        <template v-slot:additional-fields>
          <ChangeFormConsumer
            :disabled="true"
            :is-change-disabled="true"
            :module-name="moduleName"
            :resource="ticket"
            :show-no-data="false"
          />
        </template>

        <!-- default content as tabs -->
        <slot :ticket="ticket">
          <MDivider />
          <ChangeStagesContainer
            :disabled="true"
            :module-name="moduleName"
            :resource="ticket"
            @refresh="refreshTicket"
          />
          <MDivider />
          <div :key="tabsRerenderCount" class="tabs">
            <MAffix>
              <MTab v-model="activeTabKey" size="small" class="affixed-tab">
                <MTabPane
                  v-for="tab in allowedTabs"
                  :key="tab.id"
                  :tab="tab.name"
                />
              </MTab>
            </MAffix>
            <div class="my-4">
              <component
                :is="tabComponent"
                :disabled="activeTabKey !== 'work'"
                :resource-id="ticket.id"
                :watcher="ticket.watcher"
                :group-id="ticket.groupId"
                :module-name="moduleName"
                :resource="ticket"
                :update-api="update"
                v-bind="tabComponentProps"
                @relation-added="handleRelationCount"
                @relation-removed="handleRelationRemoved"
                @refresh-page="refreshPage"
                @refresh="refreshTicket"
              />
            </div>
          </div>
        </slot>
      </DetailView>
    </SuggestedFieldValuesProvider>
    <FlotoLoadError
      v-if="error"
      class="flex-1"
      :authorization-error="error.response && error.response.status === 403"
    />
    <!-- confirm modal for if ticket has secondarytickets and it gets closed -->
    <NoteRules ref="noteDrawer" :create-fn="handleNoteAdded" />
  </FlotoContentLoader>
</template>

<script>
import { stageConstants } from '@utils/status'
import Bus from '@utils/emitter'
import { FormComputed } from '@state/modules/form'
import { StatusComputed } from '@state/modules/status'
import { authComputed } from '@state/modules/auth'
import ConversationContainer from '@components/conversation/conversation-container.vue'
import TaskContainer from '@components/task/task-container.vue'
import WorkLogContainer from '@components/worklog/worklog-container'
import EmailNotificationContainer from '@components/email-notification/email-notification-container'
import RelationContainer from '@components/relation/relation-container'
import { getRelationCountApi } from '@components/relation/relation-api'
import ApprovalContainer from '@components/approval/approval-container'
import NoteRules from '@components/note-rules/note-rules'
import { CustomRulesComputed } from '@state/modules/custom-rules'
import { getChangeDetailsApi } from '../support-portal-api'
import DetailView from '@modules/ticket/components/detail/detail.vue'
import ChangeActivityTab from '@modules/ticket/components/change-activity-tab.vue'
import ChangeStagesContainer from '@modules/ticket/components/change-stages-container/change-stages-container'
import ChangeFormConsumer from '@modules/ticket/components/change-form-consumer.vue'
import { getDisabledTabsForStage } from '@modules/ticket/helpers/change-stage-tabs'
import { updateApi } from '@modules/ticket/ticket-api'
import SuggestedFieldValuesProvider from '@components/providers/suggested-field-value-provider'

export default {
  name: 'ChangeDetailView',
  components: {
    DetailView,
    NoteRules,
    ChangeStagesContainer,
    ChangeFormConsumer,
    SuggestedFieldValuesProvider,
  },
  inheritAttrs: false,
  props: {
    useTransition: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    isApproval: { type: Boolean, default: false },
  },
  data() {
    this.resourceId = this.$route.params.id
    this.moduleName = this.$constants.CHANGE
    return {
      ticket: {},
      activeTabKey: 'change_activity',
      tabsRerenderCount: 1,
      error: null,
      loading: true,
      allowedTabs: [],
      changeStage: undefined,
      relationCount: 0,
    }
  },
  computed: {
    ...authComputed,
    ...FormComputed,
    ...StatusComputed,
    ...CustomRulesComputed,
    // changeStage() {
    //   return (this[`${this.moduleName}FindStatusFromId`](this.ticket.statusId) || {}).stage
    // },
    closeStatusId() {
      if (this[`${this.moduleName}FindStatus`]) {
        return (this[`${this.moduleName}FindStatus`]('closed', 'close') || {})
          .id
      }
      return undefined
    },
    openStatusId() {
      if (this[`${this.moduleName}FindStatus`]) {
        if (this.ticket.prevStatusId) {
          return this.ticket.prevStatusId
        }
        return this[`${this.moduleName}FindStatus`](
          'in progress',
          stageConstants.PLANNING
        ).id
      }
      return undefined
    },
    resolveStatusId() {
      if (this[`${this.moduleName}FindStatus`]) {
        return this[`${this.moduleName}FindStatus`]('resolved').id
      }
      return undefined
    },
    tabComponent() {
      return (this.allowedTabs.find((t) => t.id === this.activeTabKey) || {})
        .component
    },
    tabComponentProps() {
      return (
        (this.allowedTabs.find((t) => t.id === this.activeTabKey) || {})
          .componentProps || {}
      )
    },
    isTicketResolve() {
      return this.disabled || this.ticket.statusId === this.resolveStatusId
    },
    fieldLabels() {
      const fields = this[`${this.moduleName}Fields`]({
        only: ['subject', 'description'],
      })
      if (!fields.length) {
        return {}
      }
      const titleLabel = fields.find(
        ({ paramName }) => paramName === 'subject'
      ).title
      const descriptionLabel = fields.find(
        ({ paramName }) => paramName === 'description'
      ).title
      return {
        titleLabel,
        descriptionLabel,
      }
    },
  },
  watch: {
    resourceId: {
      immediate: true,
      handler: 'getTicket',
    },
  },
  created() {
    const conversationHandler = (e, k, command, nextCommand) => {
      let availableCommands = ['note']
      if (
        availableCommands.indexOf(nextCommand) >= 0 &&
        this.activeTabKey !== 'work'
      ) {
        this.activeTabKey = 'work'
      }
    }
    Bus.$on('conversation', conversationHandler)
    this.$once('hook:beforeDestroy', () => {
      Bus.$off('conversation', conversationHandler)
    })
    this.getRelationModuleCount()
  },
  methods: {
    getRelationModuleCount() {
      return getRelationCountApi(this.moduleName, this.resourceId).then(
        (data) => {
          Object.values(data).filter((value) => {
            this.relationCount = this.relationCount + value
          })
          this.setTabs()
        }
      )
    },
    handleRelationCount() {
      this.relationCount = 0
      this.getRelationModuleCount()
    },
    handleRelationRemoved() {
      this.relationCount = 0
      this.getRelationModuleCount()
    },
    workTab() {
      const work = { id: 'work' }
      work.name = this.$tc('collaboration')
      work.component = ConversationContainer
      work.componentProps = {
        stickySearch: true,
        alwaysEditable: true,
        offsetTop: 38,
        allowedTypes: ['note', 'collaborate'],
        excludedTypes: [],
      }
      return work
    },
    setTabs(activeModule = undefined) {
      let tabs = [
        {
          id: 'change_activity',
          name: this.$tc('planning'),
          component: ChangeActivityTab,
          componentProps: {
            changeStage: this.changeStage,
          },
        },
        {
          ...this.workTab(),
        },
        {
          id: 'task',
          name: this.$tc('task', 2),
          component: TaskContainer,
          componentProps: { stickySearch: true, offsetTop: 38 },
        },
        {
          id: 'approval',
          name: this.$tc('approval', 2),
          component: ApprovalContainer,
        },
        {
          id: 'relation',
          name: `${this.$tc('relation', 2)} (${this.relationCount})`,
          component: RelationContainer,
          componentProps: {
            defaultActive: activeModule,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'work_log',
          name: this.$t('work_log'),
          component: WorkLogContainer,
          componentProps: {
            alwaysEditable: true,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'email_notification',
          name: this.$tc('notification', 2),
          component: EmailNotificationContainer,
          componentProps: {
            alwaysEditable: true,
            stickySearch: true,
            offsetTop: 38,
          },
        },
      ]
      if (
        (!this.ticket.approvalStatus ||
          this.ticket.approvalStatus === 'pre_approved') &&
        'showApprovalTab' in this.ticket &&
        !this.ticket.showApprovalTab
      ) {
        tabs = tabs.filter((t) => t.id !== 'approval')
      }
      const disabledKeys = getDisabledTabsForStage(this.changeStage)
      tabs = tabs.map((t) => {
        return {
          ...t,
          componentProps: {
            ...(t.componentProps || {}),
            disabled: disabledKeys.indexOf(t.id) >= 0,
          },
        }
      })
      this.allowedTabs = Object.freeze(tabs)
    },
    handleNoteAdded(data) {
      return this.update(
        {
          ...data,
        },
        true
      ).then(() => {
        this.$refs.noteDrawer.closeForm()
        this.refreshTicket(['work'])
      })
    },
    getTicket() {
      this.loading = true
      this.ticket = {}
      return getChangeDetailsApi(this.resourceId)
        .then((ticket) => {
          this.loading = false
          this.ticket = ticket
          this.setTabs()
          return ticket
        })
        .catch((e) => {
          if (e.response.status === 404) {
            this.$router.replace({ name: '404' })
          }
          this.error = e
          this.loading = false
        })
    },
    refreshPage() {
      this.activeTabKey = 'change_activity'
      this.refreshTicket()
    },
    refreshTicket(reRenderTabs = undefined) {
      return getChangeDetailsApi(this.resourceId).then((ticket) => {
        this.ticket = ticket
        if (reRenderTabs && reRenderTabs.indexOf(this.activeTabKey) >= 0) {
          this.tabsRerenderCount = this.tabsRerenderCount + 1
        }
        this.setTabs()
        if (
          reRenderTabs &&
          reRenderTabs.indexOf('approval') >= 0 &&
          this.allowedTabs.find((t) => t.id === 'approval') &&
          this.activeTabKey !== 'approval'
        ) {
          this.activeTabKey = 'approval'
        }
        return ticket
      })
    },
    update(change, forceUpdate = false) {
      if (Object.keys(change).length) {
        return updateApi(
          this.moduleName,
          {
            id: this.ticket.id,
            ...change,
          },
          true
        ).then((data) => {
          this.ticket = { ...this.ticket, ...data }
          return data
        })
      }
      return this.refreshTicket(['approval'])
    },
  },
}
</script>
